import { Printer } from "lucide-react";
import { Card, PageHeader, Btn } from "@/components/kit";
import { FaqList } from "@/components/FaqList";
import { VIDEO_IDEAS, HOOK_LIBRARY, SHOOT_CHECKLIST, GLOSSARY } from "@/modules/premiere-video/resources";

const BONUSES = [
  { id: "bonus-ideas", n: 1, t: "10 idées de vidéos UGC" },
  { id: "bonus-hooks", n: 2, t: "10 accroches UGC" },
  { id: "bonus-checklist", n: 3, t: "Checklist de tournage" },
  { id: "bonus-glossary", n: 4, t: "Mini-glossaire" },
  { id: "bonus-faq", n: 5, t: "FAQ" },
];

function Bonus({ id, n, title, intro, children, action }) {
  return (
    <section id={id} className="print-section scroll-mt-24" data-testid={id}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-[#9A7A45]">Bonus {n}</p>
          <h2 className="mt-1 text-2xl font-medium text-bx sm:text-3xl">{title}</h2>
          {intro && <p className="mt-1 text-soft">{intro}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

const NumList = ({ items, testId }) => (
  <Card className="p-0 sm:p-0">
    <ol data-testid={testId}>
      {items.map((it, i) => (
        <li key={it.t} className="flex gap-4 border-b border-line px-5 py-4 last:border-0 sm:px-7">
          <span className="w-7 shrink-0 font-display text-lg text-gold">{String(i + 1).padStart(2, "0")}</span>
          <div><p className="font-medium text-ink">{it.t}</p><p className="mt-0.5 text-sm leading-relaxed text-soft">{it.d}</p></div>
        </li>
      ))}
    </ol>
  </Card>
);

function printChecklist() {
  document.body.dataset.print = "checklist";
  const done = () => { delete document.body.dataset.print; window.removeEventListener("afterprint", done); };
  window.addEventListener("afterprint", done);
  window.print();
}

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <div className="resources-intro">
        <PageHeader eyebrow="Bibliothèque" title="Ressources" subtitle="Des contenus prêts à l'emploi pour t'inspirer, t'organiser et avancer sereinement." />
        <nav className="no-print mb-12 flex flex-wrap gap-2" aria-label="Bonus">
          {BONUSES.map((b) => (
            <a key={b.id} href={`#${b.id}`} className="rounded-full border border-line bg-white px-4 py-2 text-sm text-ink transition-colors hover:border-bx/40 hover:text-bx" data-testid={`jump-${b.id}`}>
              <span className="mr-1.5 font-display text-gold">{b.n}</span>{b.t}
            </a>
          ))}
        </nav>
      </div>
      <div className="space-y-16">
        <Bonus id="bonus-ideas" n={1} title="10 idées de vidéos UGC" intro="Des idées simples à réaliser avec des produits du quotidien.">
          <NumList items={VIDEO_IDEAS} testId="ideas-list" />
        </Bonus>
        <Bonus id="bonus-hooks" n={2} title="10 accroches UGC" intro="Remplace les éléments entre crochets par ton produit. Garde uniquement ce qui est vrai pour toi.">
          <NumList items={HOOK_LIBRARY} testId="hooks-list" />
        </Bonus>
        <Bonus id="bonus-checklist" n={3} title="Checklist de tournage" intro="À consulter avant, pendant et après ton tournage."
          action={<Btn variant="outline" size="sm" className="no-print" onClick={printChecklist} data-testid="print-checklist-button"><Printer className="h-4 w-4" />Imprimer la checklist</Btn>}>
          <div className="grid gap-4 md:grid-cols-3">
            {SHOOT_CHECKLIST.map((g) => (
              <Card key={g.group}>
                <h3 className="font-display text-lg text-bx">{g.group}</h3>
                <ul className="mt-3 space-y-2.5">
                  {g.items.map((it) => <li key={it} className="flex gap-2.5 text-sm text-ink"><span className="mt-0.5 h-4 w-4 shrink-0 rounded border border-gold" aria-hidden />{it}</li>)}
                </ul>
              </Card>
            ))}
          </div>
        </Bonus>
        <Bonus id="bonus-glossary" n={4} title="Mini-glossaire" intro="Les mots que tu croiseras souvent, expliqués simplement.">
          <dl className="grid gap-4 sm:grid-cols-2" data-testid="glossary-list">
            {GLOSSARY.map((g) => (
              <Card key={g.t}><dt className="font-display text-xl text-bx">{g.t}</dt><dd className="mt-2 text-sm leading-relaxed text-ink/85">{g.d}</dd></Card>
            ))}
          </dl>
        </Bonus>
        <Bonus id="bonus-faq" n={5} title="FAQ" intro="Les questions courantes des débutantes.">
          <FaqList prefix="resources-faq" />
        </Bonus>
      </div>
    </div>
  );
}
